/* eslint-disable max-lines-per-function */
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@e2n/ui';
import { Avatar, Body } from '@e2n/ui/v2';
import { HoverCard as HoverCardPrimitive } from 'radix-ui';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

export function Mitarbeiter({
  rotationsplan,
  cardElement,
}: {
  rotationsplan: Rotationsplan;
  cardElement: HTMLDivElement | null;
}) {
  const { t } = useTranslation();
  const percentage =
    (rotationsplan.abgeschlosseneStationen.length * 100) /
    rotationsplan.amountOfStationen;
  const istAbgeschlossen =
    rotationsplan.amountOfStationen > 0 &&
    rotationsplan.abgeschlosseneStationen.length ===
      rotationsplan.amountOfStationen;
  const hasUeberschneidungen = rotationsplan.hasUeberschneidungen();
  const { mitarbeiter } = rotationsplan;
  const konfliktLabel = t(
    'domain.mitarbeiter.rotationsplan.rotationsplaene.konflikt.title',
  );

  return (
    <div className="flex items-center gap-2">
      {mitarbeiter.imgUrl ? (
        <Avatar
          src={mitarbeiter.imgUrl}
          alt={mitarbeiter.getName()}
          fallbackInitials={mitarbeiter.getInitials()}
        />
      ) : (
        <Avatar type="initials" initials={mitarbeiter.getInitials()} />
      )}
      <div className="flex flex-col flex-1 gap-1 min-w-0">
        <Body className="truncate" weight="semibold">
          {mitarbeiter.vorname + ' ' + mitarbeiter.nachname}
        </Body>
        <Body className="text-labels-secondary truncate" size="sm">
          {rotationsplan.currentLehrjahr +
            ' ⋅ ' +
            rotationsplan.beruf.abkuerzung}
        </Body>
      </div>
      {hasUeberschneidungen && (
        <HoverCard openDelay={0}>
          <HoverCardTrigger
            asChild
            tabIndex={0}
            aria-label={konfliktLabel}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}>
            <span className="flex items-center justify-center size-8">
              <FontAwesomeIcon
                className="size-4.5 text-error-red-200"
                icon={faCircleExclamation}
              />
            </span>
          </HoverCardTrigger>
          <HoverCardPrimitive.Portal container={cardElement}>
            <HoverCardContent
              side="bottom"
              align="end"
              collisionBoundary={cardElement}
              collisionPadding={16}
              className="w-auto px-4 py-3 bg-backgrounds-primary border-neutrals-v2-200 rounded-xl"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}>
              <Body>{konfliktLabel}</Body>
            </HoverCardContent>
          </HoverCardPrimitive.Portal>
        </HoverCard>
      )}
      <div className="flex items-center gap-2 shrink-0">
        <ProgressRing
          percentage={percentage}
          istAbgeschlossen={istAbgeschlossen}
        />
        <div className="flex items-baseline gap-1">
          <Body weight="semibold">
            {rotationsplan.abgeschlosseneStationen.length}
          </Body>
          <Body className="text-labels-secondary" size="sm">
            / {rotationsplan.amountOfStationen}
          </Body>
        </div>
      </div>
    </div>
  );
}

function ProgressRing({
  percentage,
  istAbgeschlossen,
}: {
  percentage: number;
  istAbgeschlossen: boolean;
}) {
  return (
    <svg className="size-6" viewBox="0 0 100 100">
      <circle
        className="text-neutrals-v2-400 stroke-current"
        strokeWidth="10"
        cx="50"
        cy="50"
        r="45"
        fill="transparent"
      />
      {percentage > 0 && (
        <circle
          className={`progress-ring__circle stroke-current ${istAbgeschlossen ? 'text-accent-green-100' : 'text-brand-blue-100'}`}
          strokeWidth="10"
          strokeLinecap="round"
          cx="50"
          cy="50"
          r="45"
          fill="transparent"
          strokeDashoffset={280 - (280 * percentage) / 100}
        />
      )}
    </svg>
  );
}
